/**
 * @file schedulePeriods.js
 * @description Pure helpers for the per-day "time of day" selection on a
 *   case or position schedule (Morning / Afternoon / Flexible). A day can hold
 *   Morning AND Afternoon together; Flexible is exclusive. The value is stored
 *   as an array per day (`{ Mon: ["Morning","Afternoon"] }`). Rows saved before
 *   multi-select stored a single string (`{ Mon: "Morning" }`), so every read
 *   goes through `periodsFor`, which accepts both shapes.
 * @module lib/schedulePeriods
 */

export const DAY_ORDER = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Display order for a day's periods, regardless of click order.
const PERIOD_ORDER = ["Morning", "Afternoon", "Flexible"];

/**
 * @description Normalizes any stored per-day value (legacy string, array, or
 *   nothing) to a clean, ordered, de-duplicated array of known periods.
 * @param {Object} servicePeriods - map of day -> string | string[]
 * @param {string} day - e.g. "Mon"
 * @returns {string[]}
 */
export function periodsFor(servicePeriods, day) {
  const raw = servicePeriods ? servicePeriods[day] : null;
  const list = Array.isArray(raw) ? raw : (typeof raw === "string" && raw ? [raw] : []);
  const known = list.filter(p => PERIOD_ORDER.includes(p));
  // Flexible wins if corrupt data holds it alongside specific periods.
  if (known.includes("Flexible") && known.length > 1) return ["Flexible"];
  return PERIOD_ORDER.filter(p => known.includes(p));
}

/**
 * @description Toggles one period on a day's selection. Morning/Afternoon can
 *   coexist; choosing Flexible clears them, and choosing either of them clears
 *   Flexible. Toggling the only active period off leaves the day empty.
 * @param {string[]} current - the day's current periods
 * @param {string} period - "Morning" | "Afternoon" | "Flexible"
 * @returns {string[]}
 */
export function togglePeriod(current, period) {
  const cur = Array.isArray(current) ? current : (current ? [current] : []);
  if (cur.includes(period)) return cur.filter(p => p !== period);
  if (period === "Flexible") return ["Flexible"];
  return PERIOD_ORDER.filter(p => p !== "Flexible" && (p === period || cur.includes(p)));
}

/**
 * @description Returns a new servicePeriods map with `period` toggled on every
 *   given day. If every day already has it, it is removed from all; otherwise
 *   it is added to all (mirrors the "Quick-set all days" buttons).
 * @param {Object} servicePeriods
 * @param {string[]} days
 * @param {string} period
 * @returns {Object}
 */
export function toggleAllDays(servicePeriods, days, period) {
  const allHave = days.length > 0 && days.every(d => periodsFor(servicePeriods, d).includes(period));
  const next = { ...(servicePeriods || {}) };
  days.forEach(d => {
    const cur = periodsFor(servicePeriods, d);
    const updated = allHave
      ? cur.filter(p => p !== period)
      : (cur.includes(period) ? cur : togglePeriod(cur, period));
    if (updated.length) next[d] = updated; else delete next[d];
  });
  return next;
}

/**
 * @description Sorts a day list Mon..Sun and drops duplicates. Any unrecognized
 *   value is kept after the known days so nothing is silently lost.
 * @param {string[]} days
 * @returns {string[]}
 */
export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"];

/** True when all seven days are selected. */
export function isEveryDay(days) {
  const set = new Set(Array.isArray(days) ? days : []);
  return DAY_ORDER.every(d => set.has(d));
}

/**
 * Compact schedule shape for a listing whose every active day is exactly ["Flexible"] with no exact
 * time range. Returns null for any other combination (the caller keeps its per-day rendering), else
 * { kind: "all" } (all 7 days), { kind: "weekdays" } (exactly Mon to Fri) or { kind: "list", days }.
 * @param {string[]} days - active days
 * @param {Object} servicePeriods - day -> string | string[]
 * @param {(day: string) => boolean} hasExactRange - true when the day carries an exact time range
 */
export function flexibleScheduleShape(days, servicePeriods, hasExactRange = () => false) {
  const active = sortDays(days);
  if (!active.length) return null;
  const allFlexible = active.every(d => {
    const p = periodsFor(servicePeriods, d);
    return p.length === 1 && p[0] === "Flexible" && !hasExactRange(d);
  });
  if (!allFlexible) return null;
  if (isEveryDay(active)) return { kind: "all" };
  if (active.length === WEEKDAYS.length && WEEKDAYS.every(d => active.includes(d))) return { kind: "weekdays" };
  return { kind: "list", days: active };
}

export function sortDays(days) {
  const list = Array.isArray(days) ? days : [];
  const set = new Set(list);
  return [...DAY_ORDER.filter(d => set.has(d)), ...[...set].filter(d => !DAY_ORDER.includes(d))];
}

/**
 * @description Normalizes an entire servicePeriods map to arrays (dropping empty
 *   days) so the stored JSON has one consistent shape.
 * @param {Object} servicePeriods
 * @returns {Object}
 */
export function normalizePeriods(servicePeriods) {
  const out = {};
  Object.keys(servicePeriods || {}).forEach(d => {
    const list = periodsFor(servicePeriods, d);
    if (list.length) out[d] = list;
  });
  return out;
}
